import React, { useCallback, useEffect, useState } from 'react';
import { FormModal } from '@shared/ui/form-modal';
import { useAppDispatch } from '@app/store/store';
import { setErrorNotification, setNotification } from '@app/store/notifications';
import { helpdeskSettingsApi } from '../../../api/services/helpdesk-settings.service';
import type {
  Branch,
  EmailTestResponse,
  Mailbox,
} from '../../../types/helpdeskSettingsInterfaces';

/*
 * Филиалы техподдержки и их почтовые ящики.
 *
 * Первая ступень маршрутизации: письмо на ящик филиала → заявка филиала →
 * дежурный оператор филиала (вторая ступень). Ящиков может быть несколько,
 * ответы клиенту уходят с того ящика, на который он писал.
 */

const LANGUAGES: { code: string; label: string }[] = [
  { code: 'ru', label: 'Русский' },
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिन्दी (Hindi)' },
  { code: 'kk', label: 'Қазақша' },
  { code: 'uz', label: "O'zbek" },
  { code: 'tr', label: 'Türkçe' },
  { code: 'es', label: 'Español' },
  { code: 'de', label: 'Deutsch' },
];

const TIMEZONES = [
  'Europe/Kaliningrad',
  'Europe/Moscow',
  'Europe/Samara',
  'Asia/Yekaterinburg',
  'Asia/Omsk',
  'Asia/Krasnoyarsk',
  'Asia/Irkutsk',
  'Asia/Yakutsk',
  'Asia/Vladivostok',
  'Asia/Magadan',
  'Asia/Kamchatka',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Almaty',
  'Asia/Tashkent',
  'Europe/Minsk',
  'Europe/Istanbul',
  'UTC',
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function apiMessage(err: any, fallback: string): string {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (data.detail) return String(data.detail);
  const first = Object.entries(data)[0];
  if (first) {
    const [field, val] = first;
    return `${field}: ${Array.isArray(val) ? val.join(', ') : String(val)}`;
  }
  return fallback;
}

const inputCls =
  'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400';
const btnPrimary =
  'px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] transition-colors disabled:opacity-50';
const btnGhost =
  'px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors';

/* ─────────────────── Модалка филиала ─────────────────── */

interface Operator {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  full_name: string;
}

interface BranchModalProps {
  branch?: Branch | null;
  operators: Operator[];
  onClose: () => void;
  onSave: (data: Partial<Branch>) => Promise<void>;
}

const BranchModal: React.FC<BranchModalProps> = ({ branch, operators, onClose, onSave }) => {
  const [name, setName] = useState(branch?.name || '');
  const [code, setCode] = useState(branch?.code || '');
  const [timezone, setTimezone] = useState(branch?.timezone || 'Europe/Moscow');
  const [language, setLanguage] = useState(branch?.language || 'ru');
  const [isDefault, setIsDefault] = useState(branch?.is_default || false);
  const [isActive, setIsActive] = useState(branch?.is_active ?? true);
  const [operatorIds, setOperatorIds] = useState<number[]>(
    branch?.operators?.map((o) => o.id) || [],
  );
  const [saving, setSaving] = useState(false);

  const toggleOp = (id: number) =>
    setOperatorIds((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !code.trim()) return;
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        code: code.trim(),
        timezone,
        language,
        is_default: isDefault,
        is_active: isActive,
        operator_ids: operatorIds,
      });
    } finally {
      setSaving(false);
    }
  };

  // FormModal: окно не выше экрана, шапка и кнопки всегда видны. Поля —
  // в две колонки, подсказки короче: на ноутбуке форма помещается целиком,
  // прокручивается только список операторов.
  return (
    <FormModal
      title={branch ? 'Изменить филиал' : 'Новый филиал'}
      onClose={onClose}
      onSubmit={handleSubmit}
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} className={btnGhost}>
            Отмена
          </button>
          <button type="submit" disabled={saving} className={btnPrimary}>
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Название *</label>
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Код *</label>
          <input
            className={inputCls}
            value={code}
            onChange={(e) => setCode(e.target.value.toLowerCase())}
            placeholder="main, india, kzn…"
            pattern="[a-z0-9_-]+"
            disabled={!!branch}
          />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Часовой пояс</label>
          <select
            className={`${inputCls} bg-white`}
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          >
            {!TIMEZONES.includes(timezone) && <option value={timezone}>{timezone}</option>}
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
          <p className="text-xs leading-snug text-gray-400 mt-1">
            Для расписаний операторов без своего пояса
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Язык писем клиентам</label>
          <select
            className={`${inputCls} bg-white`}
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            {!LANGUAGES.some((l) => l.code === language) && <option value={language}>{language}</option>}
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
          <p className="text-xs leading-snug text-gray-400 mt-1">
            Автоответы и ответы клиентам — на этом языке (нужен сервис перевода)
          </p>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Операторы филиала</label>
        <div className="max-h-[min(10rem,20vh)] overflow-y-auto border border-gray-300 rounded-lg divide-y divide-gray-100">
          {operators.length === 0 ? (
            <p className="text-xs text-gray-400 px-3 py-3">Нет доступных операторов</p>
          ) : (
            operators.map((o) => (
              <label
                key={o.id}
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={operatorIds.includes(o.id)}
                  onChange={() => toggleOp(o.id)}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="text-sm text-[#2d2c2c]">
                  {o.full_name || `${o.last_name} ${o.first_name}`.trim() || o.username}
                </span>
              </label>
            ))
          )}
        </div>
      </div>
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
          Филиал по умолчанию
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Активен
        </label>
      </div>
    </FormModal>
  );
};

/* ─────────────────── Модалка ящика ─────────────────── */

interface MailboxModalProps {
  mailbox?: Mailbox | null;
  branches: Branch[];
  onClose: () => void;
  onSave: (data: Partial<Mailbox>) => Promise<void>;
}

/* Обёртка поля формы. На уровне модуля намеренно: компонент, объявленный
   внутри MailboxModal, пересоздавался при каждом рендере, React
   перемонтировал input, и поле теряло фокус после каждого символа. */
const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({
  label,
  children,
  hint,
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

const MailboxModal: React.FC<MailboxModalProps> = ({ mailbox, branches, onClose, onSave }) => {
  const [form, setForm] = useState<Partial<Mailbox>>({
    name: mailbox?.name || '',
    branch: mailbox?.branch ?? (branches.find((b) => b.is_default)?.id ?? null),
    address: mailbox?.address || '',
    from_name: mailbox?.from_name || 'Техническая поддержка',
    imap_host: mailbox?.imap_host || '',
    imap_port: mailbox?.imap_port ?? 993,
    imap_ssl: mailbox?.imap_ssl ?? true,
    imap_user: mailbox?.imap_user || '',
    imap_folder: mailbox?.imap_folder || 'INBOX',
    imap_failed_folder: mailbox?.imap_failed_folder || 'Helpdesk/Failed',
    smtp_host: mailbox?.smtp_host || '',
    smtp_port: mailbox?.smtp_port ?? 465,
    smtp_ssl: mailbox?.smtp_ssl ?? true,
    smtp_user: mailbox?.smtp_user || '',
    poll_interval_min: mailbox?.poll_interval_min ?? 2,
    is_active: mailbox?.is_active ?? true,
  });
  const [imapPassword, setImapPassword] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const set = (k: keyof Mailbox, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.address || !form.imap_host || !form.imap_user) return;
    setSaving(true);
    try {
      const data: Partial<Mailbox> = { ...form };
      if (!data.smtp_host) data.smtp_host = form.imap_host;
      if (imapPassword) data.imap_password = imapPassword;
      if (smtpPassword) data.smtp_password = smtpPassword;
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  // FormModal: шапка и кнопки закреплены, полей много — на низком экране
  // прокручиваются только поля, кнопка «Сохранить» всегда на виду.
  return (
    <FormModal
      title={mailbox ? 'Изменить ящик' : 'Новый почтовый ящик'}
      onClose={onClose}
      onSubmit={handleSubmit}
      size="xl"
      footer={
        <>
          <button type="button" onClick={onClose} className={btnGhost}>
            Отмена
          </button>
          <button type="submit" disabled={saving} className={btnPrimary}>
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Название *">
          <input className={inputCls} value={form.name || ''} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Филиал">
          <select
            className={`${inputCls} bg-white`}
            value={form.branch ?? ''}
            onChange={(e) => set('branch', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">— не привязан —</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Адрес ящика *" hint="На него пишут клиенты; с него уходят ответы">
          <input
            type="email"
            className={inputCls}
            value={form.address || ''}
            onChange={(e) => set('address', e.target.value)}
          />
        </Field>
        <Field label="Имя отправителя">
          <input className={inputCls} value={form.from_name || ''} onChange={(e) => set('from_name', e.target.value)} />
        </Field>
      </div>

      <h3 className="text-sm font-semibold text-gray-700 pt-2">IMAP (входящие)</h3>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Хост *">
          <input className={inputCls} value={form.imap_host || ''} onChange={(e) => set('imap_host', e.target.value)} />
        </Field>
        <Field label="Порт">
          <input
            type="number"
            className={inputCls}
            value={form.imap_port ?? 993}
            onChange={(e) => set('imap_port', Number(e.target.value))}
          />
        </Field>
        <Field label="SSL">
          <label className="flex items-center gap-2 h-[38px] text-sm">
            <input type="checkbox" checked={!!form.imap_ssl} onChange={(e) => set('imap_ssl', e.target.checked)} />
            Использовать SSL
          </label>
        </Field>
        <Field label="Логин *">
          <input className={inputCls} value={form.imap_user || ''} onChange={(e) => set('imap_user', e.target.value)} />
        </Field>
        <Field
          label={mailbox?.imap_password_set ? 'Пароль (установлен)' : 'Пароль'}
          hint={mailbox ? 'Оставьте пустым, чтобы не менять' : undefined}
        >
          <input
            type="password"
            className={inputCls}
            value={imapPassword}
            onChange={(e) => setImapPassword(e.target.value)}
            autoComplete="new-password"
          />
        </Field>
        <Field label="Интервал опроса, мин">
          <input
            type="number"
            min={1}
            max={60}
            className={inputCls}
            value={form.poll_interval_min ?? 2}
            onChange={(e) => set('poll_interval_min', Number(e.target.value))}
          />
        </Field>
        <Field label="Папка входящих">
          <input className={inputCls} value={form.imap_folder || ''} onChange={(e) => set('imap_folder', e.target.value)} />
        </Field>
        <Field label="Папка для необработанных" hint="Письма, которые не удалось разобрать">
          <input
            className={inputCls}
            value={form.imap_failed_folder || ''}
            onChange={(e) => set('imap_failed_folder', e.target.value)}
          />
        </Field>
      </div>

      <h3 className="text-sm font-semibold text-gray-700 pt-2">SMTP (исходящие)</h3>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Хост" hint="Пусто — как у IMAP">
          <input className={inputCls} value={form.smtp_host || ''} onChange={(e) => set('smtp_host', e.target.value)} />
        </Field>
        <Field label="Порт">
          <input
            type="number"
            className={inputCls}
            value={form.smtp_port ?? 465}
            onChange={(e) => set('smtp_port', Number(e.target.value))}
          />
        </Field>
        <Field label="SSL">
          <label className="flex items-center gap-2 h-[38px] text-sm">
            <input type="checkbox" checked={!!form.smtp_ssl} onChange={(e) => set('smtp_ssl', e.target.checked)} />
            SSL (иначе STARTTLS)
          </label>
        </Field>
        <Field label="Логин" hint="Пусто — как у IMAP">
          <input className={inputCls} value={form.smtp_user || ''} onChange={(e) => set('smtp_user', e.target.value)} />
        </Field>
        <Field
          label={mailbox?.smtp_password_set ? 'Пароль (установлен)' : 'Пароль'}
          hint="Пусто — как у IMAP"
        >
          <input
            type="password"
            className={inputCls}
            value={smtpPassword}
            onChange={(e) => setSmtpPassword(e.target.value)}
            autoComplete="new-password"
          />
        </Field>
        <Field label="Активен">
          <label className="flex items-center gap-2 h-[38px] text-sm">
            <input type="checkbox" checked={!!form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
            Опрашивать и отвечать
          </label>
        </Field>
      </div>
    </FormModal>
  );
};

/* ─────────────────── Секция ─────────────────── */

const BranchesSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [loading, setLoading] = useState(true);
  const [editBranch, setEditBranch] = useState<Branch | null | undefined>(undefined);
  const [editMailbox, setEditMailbox] = useState<Mailbox | null | undefined>(undefined);
  const [testing, setTesting] = useState<number | null>(null);
  const [testResult, setTestResult] = useState<Record<number, EmailTestResponse>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, m, o] = await Promise.all([
        helpdeskSettingsApi.getBranches(),
        helpdeskSettingsApi.getMailboxes(),
        helpdeskSettingsApi.getAvailableOperators(),
      ]);
      setBranches(b.data);
      setMailboxes(m.data);
      setOperators(o.data);
    } catch (err) {
      dispatch(setErrorNotification(apiMessage(err, 'Не удалось загрузить филиалы')));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const saveBranch = async (data: Partial<Branch>) => {
    try {
      if (editBranch) await helpdeskSettingsApi.updateBranch(editBranch.id, data);
      else await helpdeskSettingsApi.createBranch(data);
      dispatch(setNotification('Филиал сохранён'));
      setEditBranch(undefined);
      load();
    } catch (err) {
      dispatch(setErrorNotification(apiMessage(err, 'Не удалось сохранить филиал')));
    }
  };

  const saveMailbox = async (data: Partial<Mailbox>) => {
    try {
      if (editMailbox) await helpdeskSettingsApi.updateMailbox(editMailbox.id, data);
      else await helpdeskSettingsApi.createMailbox(data);
      dispatch(setNotification('Ящик сохранён'));
      setEditMailbox(undefined);
      load();
    } catch (err) {
      dispatch(setErrorNotification(apiMessage(err, 'Не удалось сохранить ящик')));
    }
  };

  const deleteMailbox = async (m: Mailbox) => {
    if (!window.confirm(`Удалить ящик ${m.address}?`)) return;
    try {
      await helpdeskSettingsApi.deleteMailbox(m.id);
      load();
    } catch (err) {
      dispatch(setErrorNotification(apiMessage(err, 'Не удалось удалить ящик')));
    }
  };

  const testMailbox = async (m: Mailbox) => {
    setTesting(m.id);
    try {
      const res = await helpdeskSettingsApi.testMailbox(m.id, 'both');
      setTestResult((r) => ({ ...r, [m.id]: res.data }));
      load();
    } catch (err) {
      dispatch(setErrorNotification(apiMessage(err, 'Проверка не удалась')));
    } finally {
      setTesting(null);
    }
  };

  const sendTestMail = async (m: Mailbox) => {
    // eslint-disable-next-line no-alert
    const to = window.prompt('На какой адрес отправить тестовое письмо?', '');
    if (!to) return;
    setTesting(m.id);
    try {
      const res = await helpdeskSettingsApi.sendTestMail(m.id, to.trim());
      setTestResult((r) => ({ ...r, [m.id]: res.data }));
      if (res.data.send?.success) dispatch(setNotification(res.data.send.message));
      else dispatch(setErrorNotification(res.data.send?.message || 'Отправка не удалась'));
      load();
    } catch (err) {
      dispatch(setErrorNotification(apiMessage(err, 'Отправка не удалась')));
    } finally {
      setTesting(null);
    }
  };

  const pollMailbox = async (m: Mailbox) => {
    try {
      await helpdeskSettingsApi.pollMailbox(m.id);
      dispatch(setNotification('Опрос ящика запущен'));
    } catch (err) {
      dispatch(setErrorNotification(apiMessage(err, 'Не удалось запустить опрос')));
    }
  };

  const statusDot = (ok: boolean, title: string) => (
    <span
      title={title}
      className={`inline-block w-2 h-2 rounded-full ${ok ? 'bg-green-500' : 'bg-gray-300'}`}
    />
  );

  return (
    <div className="space-y-6">
      {/* Филиалы */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-[#2d2c2c]">Филиалы поддержки</h2>
            <p className="text-sm text-gray-500 mt-1">
              Письмо на ящик филиала становится заявкой филиала и уходит его дежурному
              оператору. Операторы по умолчанию видят заявки своих филиалов; остальные —
              через фильтр «Филиал» в списке заявок.
            </p>
          </div>
          <button className={btnPrimary} onClick={() => setEditBranch(null)}>
            + Филиал
          </button>
        </div>
        {loading ? (
          <p className="text-sm text-gray-400">Загрузка…</p>
        ) : branches.length === 0 ? (
          <p className="text-sm text-gray-400">Филиалов пока нет</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {branches.map((b) => (
              <div key={b.id} className="flex items-center justify-between py-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-[#2d2c2c]">{b.name}</span>
                    <span className="text-xs text-gray-400">{b.code}</span>
                    {b.is_default && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-[#e3282a]">
                        по умолчанию
                      </span>
                    )}
                    {!b.is_active && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                        неактивен
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {b.timezone} · {b.language || 'ru'} · операторов: {b.operators.length} · ящиков: {b.mailboxes.length}
                  </p>
                </div>
                <button
                  className="text-sm font-medium text-[#e3282a] hover:text-[#c82022]"
                  onClick={() => setEditBranch(b)}
                >
                  Изменить
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ящики */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-[#2d2c2c]">Почтовые ящики</h2>
            <p className="text-sm text-gray-500 mt-1">
              Каждый ящик опрашивается по IMAP и отвечает клиентам по SMTP со своего адреса.
              Пока ящиков нет, используется единый ящик из раздела «Email-канал».
            </p>
          </div>
          <button className={btnPrimary} onClick={() => setEditMailbox(null)}>
            + Ящик
          </button>
        </div>
        {loading ? (
          <p className="text-sm text-gray-400">Загрузка…</p>
        ) : mailboxes.length === 0 ? (
          <p className="text-sm text-gray-400">Ящиков пока нет</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {mailboxes.map((m) => (
              <div key={m.id} className="py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      {statusDot(m.imap_connected, m.imap_connected ? 'IMAP подключен' : 'IMAP не проверен')}
                      {statusDot(m.smtp_connected, m.smtp_connected ? 'SMTP подключен' : 'SMTP не проверен')}
                      <span className="font-medium text-[#2d2c2c]">{m.address}</span>
                      <span className="text-xs text-gray-400">{m.name}</span>
                      {m.branch_name && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          {m.branch_name}
                        </span>
                      )}
                      {!m.is_active && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                          выключен
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      IMAP {m.imap_host}:{m.imap_port} · SMTP {m.smtp_host}:{m.smtp_port} · опрос
                      каждые {m.poll_interval_min} мин
                      {m.last_check && ` · проверка ${new Date(m.last_check).toLocaleString('ru-RU')}`}
                    </p>
                    {m.last_error && (
                      <p className="text-xs text-red-500 mt-0.5">{m.last_error}</p>
                    )}
                    {testResult[m.id] && (
                      <p className="text-xs mt-0.5">
                        {testResult[m.id].imap && (
                          <span className={testResult[m.id].imap!.success ? 'text-green-600' : 'text-red-500'}>
                            IMAP: {testResult[m.id].imap!.message}
                          </span>
                        )}{' '}
                        {testResult[m.id].smtp && (
                          <span className={testResult[m.id].smtp!.success ? 'text-green-600' : 'text-red-500'}>
                            SMTP: {testResult[m.id].smtp!.message}
                          </span>
                        )}
                        {testResult[m.id].send && (
                          <span className={testResult[m.id].send!.success ? 'text-green-600' : 'text-red-500'}>
                            Отправка: {testResult[m.id].send!.message}
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm font-medium">
                    <button
                      className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                      disabled={testing === m.id}
                      onClick={() => testMailbox(m)}
                    >
                      {testing === m.id ? 'Проверка…' : 'Проверить'}
                    </button>
                    <button
                      className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                      disabled={testing === m.id}
                      onClick={() => sendTestMail(m)}
                      title="Реально отправить письмо с этого ящика — так же, как уходят автоответы клиентам"
                    >
                      Тест-письмо
                    </button>
                    <button className="text-gray-600 hover:text-gray-900" onClick={() => pollMailbox(m)}>
                      Опросить
                    </button>
                    <button className="text-[#e3282a] hover:text-[#c82022]" onClick={() => setEditMailbox(m)}>
                      Изменить
                    </button>
                    <button className="text-gray-400 hover:text-red-500" onClick={() => deleteMailbox(m)}>
                      Удалить
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editBranch !== undefined && (
        <BranchModal
          branch={editBranch}
          operators={operators}
          onClose={() => setEditBranch(undefined)}
          onSave={saveBranch}
        />
      )}
      {editMailbox !== undefined && (
        <MailboxModal
          mailbox={editMailbox}
          branches={branches}
          onClose={() => setEditMailbox(undefined)}
          onSave={saveMailbox}
        />
      )}
    </div>
  );
};

export default BranchesSettings;
